"use client";

import { LayoutGridIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../../cn";
import { Badge } from "../badge";
import { EmptyState } from "../empty-state";
import { CARD_HOVER } from "../states";
import type { StatusTone } from "./task-meta";

// The gallery view (spec M2 views; Work OS has none, so this follows the board card recipe of
// design spec §6.5 at a larger size): a responsive grid of hairline cards (min 220 wide, gap 16)
// with a 128 px cover on --sunken when the item has an image (wave 7: no empty cover box, the
// card starts with its title, its icon beside it), the title in two lines, a
// muted description, badges and a meta row. The title is the card's one link or button,
// stretched over the card; selection is --primary-soft with a --primary border.

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type GalleryItem = {
  id: string;
  title: string;
  description?: string;
  /** Image URL for the cover. */
  cover?: string;
  icon?: IconType;
  badges?: { label: string; tone?: StatusTone }[];
  /** T4-VIEWS-MEDIA: the view's card fields as short texts, one muted line each. */
  fields?: string[];
  meta?: React.ReactNode;
  href?: string;
};

export type GalleryViewProps = Omit<React.ComponentProps<"ul">, "children"> & {
  items: GalleryItem[];
  onOpen?: (id: string) => void;
  selectedId?: string;
  empty?: React.ReactNode;
};

export function GalleryView({
  items,
  onOpen,
  selectedId,
  empty,
  className,
  "aria-label": ariaLabel = "Gallery",
  ...props
}: GalleryViewProps) {
  if (items.length === 0)
    return (
      <div data-slot="gallery-view" className={className}>
        {empty ?? <EmptyState icon={LayoutGridIcon} title="Nothing here yet" variant="card" />}
      </div>
    );
  return (
    <ul
      data-slot="gallery-view"
      aria-label={ariaLabel}
      className={cn(
        "m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 p-0 max-sm:grid-cols-1",
        className,
      )}
      {...props}
    >
      {items.map((item) => {
        const Icon = item.cover ? undefined : item.icon;
        const selected = item.id === selectedId;
        const titleCls =
          "line-clamp-2 text-left text-base font-medium text-foreground outline-none after:absolute after:inset-0 after:content-['']";
        return (
          <li
            key={item.id}
            data-slot="gallery-view-card"
            data-selected={selected || undefined}
            className={cn(
              "relative flex flex-col overflow-hidden rounded-lg border border-border bg-card data-selected:border-primary data-selected:bg-primary-soft",
              CARD_HOVER,
              "has-[[data-card-title]:focus-visible]:outline-2 has-[[data-card-title]:focus-visible]:outline-offset-2 has-[[data-card-title]:focus-visible]:outline-ring has-[[data-card-title]:focus-visible]:outline-solid",
            )}
          >
            {item.cover ? (
              <div className="flex h-32 items-center justify-center border-b border-border-subtle bg-sunken">
                <img src={item.cover} alt="" className="size-full object-cover" />
              </div>
            ) : null}
            <div className="flex flex-1 flex-col gap-1.5 p-3">
              {Icon ? <Icon aria-hidden={true} className="size-4 text-faint" /> : null}
              {item.href && !onOpen ? (
                <a data-card-title="" href={item.href} className={titleCls}>
                  {item.title}
                </a>
              ) : onOpen ? (
                <button
                  data-card-title=""
                  type="button"
                  aria-current={selected ? "true" : undefined}
                  onClick={() => onOpen(item.id)}
                  className={cn(titleCls, "cursor-pointer")}
                >
                  {item.title}
                </button>
              ) : (
                <span className={titleCls}>{item.title}</span>
              )}
              {item.description ? (
                <p
                  className={cn(
                    "m-0 line-clamp-2 text-xs",
                    selected ? "text-secondary" : "text-muted-foreground",
                  )}
                >
                  {item.description}
                </p>
              ) : null}
              {item.badges?.length ? (
                <div className="flex flex-wrap gap-1">
                  {item.badges.map((b) => (
                    <Badge key={b.label} variant={b.tone ?? "neutral"}>
                      {b.label}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {item.fields?.length ? (
                <ul
                  data-slot="gallery-view-fields"
                  className={cn(
                    "m-0 flex list-none flex-col gap-0.5 p-0 text-xs",
                    selected ? "text-secondary" : "text-muted-foreground",
                  )}
                >
                  {item.fields.map((f) => (
                    <li key={f} className="truncate">
                      {f}
                    </li>
                  ))}
                </ul>
              ) : null}
              {item.meta ? (
                <div
                  className={cn(
                    "mt-auto flex items-center gap-2 pt-1 text-xs tabular-nums",
                    selected ? "text-secondary" : "text-muted-foreground",
                  )}
                >
                  {item.meta}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
